import { useState, useEffect, useRef } from 'react';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useGlobalWalletSignerAccount } from '@abstract-foundation/agw-react';
import { useAccount, useConnect } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useAppContext } from '../contexts/hooks';
import { ErrorMessage } from '../components/ErrorMessage';
import { trackEvent } from '../lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { OdsBadge } from '@/components/orderly/OdsBadge';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

const MOCK_ADDRESSES = {
  evm: '0x742d35Cc6634C0532925a3b844Bc9e7595f2bD18',
  solana: '7EcDhSYGxXyscszYEp35KHN8vvw3svAuLKTzXwCFLtV',
};

const ABSTRACT_CONNECTOR_ID = 'xyz.abs.privy';

export default function ConnectWallet() {
  const { state, dispatch } = useAppContext();
  const { openConnectModal } = useConnectModal();
  const { address: evmAddress, connector, isConnected } = useAccount();
  const { address: abstractSignerAddress } = useGlobalWalletSignerAccount();
  const { connectAsync, connectors } = useConnect();
  const { connected: solanaConnected, disconnect: disconnectSolana, publicKey: solanaPublicKey } = useWallet();
  const { visible: solanaModalVisible, setVisible: setSolanaModalVisible } = useWalletModal();
  const [error, setError] = useState<string | null>(null);
  const pendingConnect = useRef<'evm' | 'solana' | null>(null);
  const pendingAbstractConnect = useRef(false);
  const prevSolanaModalVisible = useRef(false);
  const solanaAddress = solanaPublicKey?.toBase58();
  const isAbstractWallet = connector?.id === ABSTRACT_CONNECTOR_ID || connector?.name === 'Abstract';
  const activeEvmAddress = isAbstractWallet ? abstractSignerAddress : evmAddress;

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'ConnectWallet',
      page: 'connect',
      chain: state.chain ?? undefined,
    });
  }, [state.chain]);

  useEffect(() => {
    if (pendingConnect.current === 'evm' && !solanaConnected && openConnectModal) {
      pendingConnect.current = null;
      trackEvent('modal_opened', { modal: 'evm_wallet_connect', component: 'ConnectWallet' });
      openConnectModal();
    }
  }, [openConnectModal, solanaConnected]);

  useEffect(() => {
    if (pendingConnect.current === 'solana' && !isConnected) {
      pendingConnect.current = null;
      setSolanaModalVisible(true);
    }
  }, [isConnected, setSolanaModalVisible]);

  useEffect(() => {
    if (!pendingAbstractConnect.current || !isAbstractWallet || !abstractSignerAddress) return;
    pendingAbstractConnect.current = false;
    dispatch({ type: 'CONNECT_WALLET', chain: 'evm', address: abstractSignerAddress });
  }, [abstractSignerAddress, dispatch, isAbstractWallet]);

  useEffect(() => {
    if (!prevSolanaModalVisible.current && solanaModalVisible) {
      trackEvent('modal_opened', { modal: 'solana_wallet_connect', component: 'ConnectWallet' });
    }

    if (prevSolanaModalVisible.current && !solanaModalVisible) {
      trackEvent('modal_closed', {
        modal: 'solana_wallet_connect',
        component: 'ConnectWallet',
        action: solanaConnected ? 'connected' : 'dismissed',
      });
    }

    prevSolanaModalVisible.current = solanaModalVisible;
  }, [solanaModalVisible, solanaConnected]);

  async function connectEvmWallet(walletGroup: 'evm' | 'abstract' = 'evm') {
    trackEvent('connect_wallet_click', { chain: 'evm', wallet_group: walletGroup });

    if (USE_MOCK) {
      dispatch({ type: 'CONNECT_WALLET', chain: 'evm', address: MOCK_ADDRESSES.evm });
      return;
    }

    setError(null);
    dispatch({ type: 'SET_PREFERRED_CHAIN', chain: 'evm' });

    if (state.chain !== 'evm' && (state.connectedWallets.evm || activeEvmAddress)) {
      dispatch({ type: 'SET_ACTIVE_WALLET', chain: 'evm' });
      return;
    }

    if (isConnected && activeEvmAddress) {
      dispatch({ type: 'CONNECT_WALLET', chain: 'evm', address: activeEvmAddress });
      return;
    }

    if (!openConnectModal) {
      pendingConnect.current = 'evm';
      return;
    }

    trackEvent('modal_opened', { modal: 'evm_wallet_connect', component: 'ConnectWallet' });
    openConnectModal();
  }

  async function handleEVMClick() {
    await connectEvmWallet('evm');
  }

  async function handleAbstractClick() {
    trackEvent('connect_wallet_click', { chain: 'evm', wallet_group: 'abstract' });

    if (USE_MOCK) {
      dispatch({ type: 'CONNECT_WALLET', chain: 'evm', address: MOCK_ADDRESSES.evm });
      return;
    }

    setError(null);
    dispatch({ type: 'SET_PREFERRED_CHAIN', chain: 'evm' });

    const abstractConnector = connectors.find(
      (connector) => connector.id === ABSTRACT_CONNECTOR_ID || connector.name === 'Abstract',
    );

    if (!abstractConnector) {
      setError('Abstract wallet connector is not available.');
      return;
    }

    try {
      const result = await connectAsync({ connector: abstractConnector });
      // AGW exposes [smart account, signer EOA]; broker lookup must use the signer.
      const address = result.accounts[1] ?? abstractSignerAddress;
      if (address) {
        pendingAbstractConnect.current = false;
        dispatch({ type: 'CONNECT_WALLET', chain: 'evm', address });
        return;
      }
      pendingAbstractConnect.current = true;
    } catch (connectError) {
      pendingAbstractConnect.current = false;
      setError(connectError instanceof Error ? connectError.message : 'Failed to connect Abstract wallet.');
    }
  }

  async function connectSolana() {
    trackEvent('connect_wallet_click', { chain: 'solana' });

    if (USE_MOCK) {
      dispatch({ type: 'CONNECT_WALLET', chain: 'solana', address: MOCK_ADDRESSES.solana });
      return;
    }

    setError(null);
    dispatch({ type: 'SET_PREFERRED_CHAIN', chain: 'solana' });

    if (state.chain !== 'solana' && state.connectedWallets.solana) {
      dispatch({ type: 'SET_ACTIVE_WALLET', chain: 'solana' });
      return;
    }

    if (solanaConnected) {
      const address = state.connectedWallets.solana ?? solanaAddress;
      if (address) {
        dispatch({ type: 'CONNECT_WALLET', chain: 'solana', address });
        return;
      }
      await disconnectSolana();
    }

    trackEvent('modal_opened', { modal: 'solana_wallet_connect', component: 'ConnectWallet' });
    setSolanaModalVisible(true);
  }

  return (
    <div className="flex min-h-[calc(100vh-220px)] items-center justify-center">
      <Card className="relative w-full max-w-[520px] overflow-hidden border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0 shadow-[var(--shadow-popout)]">
        <CardContent className="p-8 text-center sm:p-12">
          <OdsBadge type="brand" className="mx-auto mb-5">Emergency Exit</OdsBadge>
          <h2 className="font-display text-3xl leading-tight text-[var(--fg-base)] sm:text-4xl">Connect wallet</h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[var(--fg-secondary)]">
            Connect your wallet to close all positions and withdraw funds from a DEX.
          </p>

          {error && <div className="mt-6"><ErrorMessage message={error} onRetry={() => setError(null)} /></div>}

          <div className="mt-8 grid gap-3 text-left">
            <button
              className="group rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-secondary)] p-5 text-left transition hover:border-[var(--border-primary)] hover:bg-[var(--bg-primary-subtle)] disabled:pointer-events-none disabled:opacity-50"
              onClick={handleEVMClick}
            >
              <span className="block text-lg font-semibold text-[var(--fg-base)]">EVM</span>
              <span className="mt-1 block text-sm text-[var(--fg-secondary)]">MetaMask, WalletConnect, OKX, Coinbase, Rabby, Phantom</span>
            </button>

            <button
              className="group rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-secondary)] p-5 text-left transition hover:border-[var(--border-primary)] hover:bg-[var(--bg-primary-subtle)] disabled:pointer-events-none disabled:opacity-50"
              onClick={connectSolana}
            >
              <span className="block text-lg font-semibold text-[var(--fg-base)]">Solana</span>
              <span className="mt-1 block text-sm text-[var(--fg-secondary)]">MetaMask, Solflare, Phantom, Ledger</span>
            </button>

            <button
              className="group rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-secondary)] p-5 text-left transition hover:border-[var(--border-primary)] hover:bg-[var(--bg-primary-subtle)] disabled:pointer-events-none disabled:opacity-50"
              onClick={handleAbstractClick}
            >
              <span className="block text-lg font-semibold text-[var(--fg-base)]">Abstract</span>
              <span className="mt-1 block text-sm text-[var(--fg-secondary)]">Abstract Global Wallet</span>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
